import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Briefcase, Plus, Trash, Link as LinkIcon, Check, X, CurrencyInr } from '@phosphor-icons/react';

const Badge = ({ children, tone = 'slate' }) => (
  <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>{children}</span>
);
const Card = ({ children, className = '', ...p }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-4 ${className}`} {...p}>{children}</div>
);
const Label = ({ children }) => <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">{children}</p>;
const money = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const STATUS_TONE = { draft: 'slate', approved: 'blue', received: 'green', closed: 'slate', cancelled: 'rose', submitted: 'amber', paid: 'green', rejected: 'rose', active: 'green', blocked: 'rose' };

const OverviewTab = () => {
  const { data } = useQuery({ queryKey: ['proc-overview'], queryFn: () => api.get('/procurement/overview').then((r) => r.data) });
  if (!data) return <p className="text-sm text-slate-400">Loading...</p>;
  const kpis = [['Vendors', data.vendors, 'blue'], ['Open POs', data.open_pos, 'blue'], ['Open PO value', money(data.open_po_value), 'slate'], ['Unpaid invoices', data.unpaid_invoices, 'amber'], ['Unpaid value', money(data.unpaid_value), 'amber'], ['Paid to date', money(data.ytd_paid), 'green']];
  return (
    <div data-testid="proc-overview">
      {data.pending_portal > 0 && <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-sm text-amber-800">{data.pending_portal} invoice(s) submitted via the vendor portal awaiting review.</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {kpis.map(([l, v, t]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`proc-kpi-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <p className="font-mono-label text-[9px] uppercase text-slate-400">{l}</p>
            <p className={`font-display text-xl font-extrabold text-${t}-600`}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const VendorsTab = () => {
  const qc = useQueryClient();
  const { data: vendors } = useQuery({ queryKey: ['vendors'], queryFn: () => api.get('/procurement/vendors').then((r) => r.data) });
  const [f, setF] = useState({ name: '', category: '', email: '', phone: '', tax_id: '' });
  const create = useMutation({ mutationFn: () => api.post('/procurement/vendors', f), onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendors', 'proc-overview'] }); setF({ name: '', category: '', email: '', phone: '', tax_id: '' }); toast.success('Vendor added'); }, onError: (e) => toast.error(errMsg(e)) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/procurement/vendors/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendors'] }); toast.success('Removed'); }, onError: (e) => toast.error(errMsg(e)) });
  const block = useMutation({ mutationFn: ({ id, status, name }) => api.patch(`/procurement/vendors/${id}`, { name, status }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendors'] }); toast.success('Updated'); }, onError: (e) => toast.error(errMsg(e)) });
  const link = useMutation({ mutationFn: (id) => api.post(`/procurement/vendors/${id}/portal-link`).then((r) => r.data), onSuccess: (d) => { navigator.clipboard?.writeText(d.portal_url); toast.success('Vendor portal link copied'); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div data-testid="proc-vendors">
      <Card className="mb-4">
        <Label>Add vendor</Label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="vendor-name" />
          <Input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <Input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <Input placeholder="Tax ID / GSTIN" value={f.tax_id} onChange={(e) => setF({ ...f, tax_id: e.target.value })} />
        </div>
        <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={!f.name || create.isPending} data-testid="vendor-add-btn"><Plus className="mr-1" /> Add vendor</Button>
      </Card>
      <div className="space-y-2">
        {(vendors || []).map((v) => (
          <Card key={v.id} data-testid={`vendor-${v.id}`}>
            <div className="flex items-center gap-3 flex-wrap text-sm">
              <span className="font-semibold">{v.name}</span>
              <Badge tone="slate">{v.code}</Badge>
              {v.category && <span className="text-xs text-slate-400">{v.category}</span>}
              <Badge tone={STATUS_TONE[v.status]}>{v.status}</Badge>
              {v.email && <span className="text-xs text-slate-400">{v.email}</span>}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => link.mutate(v.id)} data-testid={`vendor-portal-${v.id}`}><LinkIcon className="mr-1" /> Portal link</Button>
                <Button size="sm" variant="outline" onClick={() => block.mutate({ id: v.id, name: v.name, status: v.status === 'blocked' ? 'active' : 'blocked' })}>{v.status === 'blocked' ? 'Unblock' : 'Block'}</Button>
                <Button size="sm" variant="outline" onClick={() => del.mutate(v.id)}><Trash /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const POsTab = () => {
  const qc = useQueryClient();
  const { data: vendors } = useQuery({ queryKey: ['vendors'], queryFn: () => api.get('/procurement/vendors').then((r) => r.data) });
  const { data: pos } = useQuery({ queryKey: ['pos'], queryFn: () => api.get('/procurement/pos').then((r) => r.data) });
  const [vendor, setVendor] = useState('');
  const [taxPct, setTaxPct] = useState(0);
  const [lines, setLines] = useState([{ desc: '', qty: 1, unit_price: 0 }]);
  const create = useMutation({
    mutationFn: () => api.post('/procurement/pos', { vendor_id: vendor, tax_pct: Number(taxPct), lines: lines.map((l) => ({ desc: l.desc, qty: Number(l.qty), unit_price: Number(l.unit_price) })) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pos', 'proc-overview'] }); setLines([{ desc: '', qty: 1, unit_price: 0 }]); setVendor(''); toast.success('PO created'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const act = useMutation({ mutationFn: ({ id, action }) => api.post(`/procurement/pos/${id}/${action}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['pos', 'proc-overview'] }); toast.success('Updated'); }, onError: (e) => toast.error(errMsg(e)) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/procurement/pos/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['pos'] }); toast.success('Deleted'); }, onError: (e) => toast.error(errMsg(e)) });
  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
  return (
    <div data-testid="proc-pos">
      <Card className="mb-4">
        <Label>New purchase order</Label>
        <select className="w-full md:w-1/2 border border-slate-200 rounded-md h-9 px-2 text-sm mb-2" value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="po-vendor">
          <option value="">Select vendor...</option>
          {(vendors || []).filter((v) => v.status !== 'blocked').map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 mb-2 items-center">
            <Input placeholder="Item / description" value={l.desc} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} data-testid={`po-line-desc-${i}`} />
            <Input className="w-20" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
            <Input className="w-28" type="number" placeholder="Unit price" value={l.unit_price} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
            {lines.length > 1 && <button className="text-rose-500" onClick={() => setLines(lines.filter((_, j) => j !== i))}><X /></button>}
          </div>
        ))}
        <div className="flex items-center gap-3 flex-wrap">
          <button className="text-blue-600 text-xs flex items-center gap-1" onClick={() => setLines([...lines, { desc: '', qty: 1, unit_price: 0 }])}><Plus /> add line</button>
          <label className="text-xs text-slate-500 flex items-center gap-1">Tax % <Input className="w-16 h-7" type="number" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} /></label>
          <span className="text-sm ml-auto">Subtotal: <b>{money(total)}</b> · Total incl tax: <b>{money(total * (1 + Number(taxPct) / 100))}</b></span>
        </div>
        <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={!vendor || !lines[0].desc || create.isPending} data-testid="po-create-btn">Create PO</Button>
      </Card>
      <div className="space-y-2">
        {(pos || []).map((p) => (
          <Card key={p.id} data-testid={`po-${p.id}`}>
            <div className="flex items-center gap-3 flex-wrap text-sm mb-1">
              <span className="font-semibold">{p.po_number}</span>
              <span className="text-slate-500">{p.vendor_name}</span>
              <Badge tone={STATUS_TONE[p.status]}>{p.status}</Badge>
              <span className="font-bold">{money(p.total)}</span>
              <div className="ml-auto flex gap-2">
                {p.status === 'draft' && <Button size="sm" onClick={() => act.mutate({ id: p.id, action: 'approve' })} data-testid={`po-approve-${p.id}`}><Check className="mr-1" /> Approve</Button>}
                {p.status === 'approved' && <Button size="sm" onClick={() => act.mutate({ id: p.id, action: 'receive' })} data-testid={`po-receive-${p.id}`}>Receive</Button>}
                {p.status === 'draft' && <Button size="sm" variant="outline" onClick={() => del.mutate(p.id)}><Trash /></Button>}
              </div>
            </div>
            <div className="text-xs text-slate-500">{(p.lines || []).map((l, i) => <span key={i} className="mr-3">{l.desc} ({l.qty}×{money(l.unit_price)})</span>)}</div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const InvoicesTab = () => {
  const qc = useQueryClient();
  const { data: vendors } = useQuery({ queryKey: ['vendors'], queryFn: () => api.get('/procurement/vendors').then((r) => r.data) });
  const { data: pos } = useQuery({ queryKey: ['pos'], queryFn: () => api.get('/procurement/pos').then((r) => r.data) });
  const { data: invoices } = useQuery({ queryKey: ['vendor-invoices'], queryFn: () => api.get('/procurement/invoices').then((r) => r.data) });
  const [f, setF] = useState({ vendor_id: '', invoice_number: '', amount: '', po_id: '' });
  const create = useMutation({ mutationFn: () => api.post('/procurement/invoices', { ...f, amount: Number(f.amount), po_id: f.po_id || null }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendor-invoices', 'proc-overview'] }); setF({ vendor_id: '', invoice_number: '', amount: '', po_id: '' }); toast.success('Invoice recorded'); }, onError: (e) => toast.error(errMsg(e)) });
  const act = useMutation({ mutationFn: ({ id, action }) => api.post(`/procurement/invoices/${id}/${action}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendor-invoices', 'proc-overview'] }); toast.success('Updated'); }, onError: (e) => toast.error(errMsg(e)) });
  const vendorPos = (pos || []).filter((p) => p.vendor_id === f.vendor_id && ['approved', 'received'].includes(p.status));
  return (
    <div data-testid="proc-invoices">
      <Card className="mb-4">
        <Label>Record a vendor invoice</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select className="border border-slate-200 rounded-md h-9 px-2 text-sm" value={f.vendor_id} onChange={(e) => setF({ ...f, vendor_id: e.target.value, po_id: '' })} data-testid="inv-vendor">
            <option value="">Vendor...</option>
            {(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <Input placeholder="Invoice #" value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="inv-number" />
          <Input type="number" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="inv-amount" />
          <select className="border border-slate-200 rounded-md h-9 px-2 text-sm" value={f.po_id} onChange={(e) => setF({ ...f, po_id: e.target.value })}>
            <option value="">No PO</option>
            {vendorPos.map((p) => <option key={p.id} value={p.id}>{p.po_number} ({money(p.total)})</option>)}
          </select>
        </div>
        <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={!f.vendor_id || !f.invoice_number || !f.amount || create.isPending} data-testid="inv-create-btn">Record invoice</Button>
      </Card>
      <div className="space-y-2">
        {(invoices || []).map((i) => (
          <Card key={i.id} data-testid={`inv-${i.id}`}>
            <div className="flex items-center gap-3 flex-wrap text-sm">
              <span className="font-semibold">{i.invoice_number}</span>
              <span className="text-slate-500">{i.vendor_name}</span>
              {i.po_number && <Badge tone="slate">{i.po_number}</Badge>}
              <span className="font-bold">{money(i.amount)}</span>
              <Badge tone={STATUS_TONE[i.status]}>{i.status}</Badge>
              {i.source === 'portal' && <Badge tone="blue">portal</Badge>}
              {i.match?.over_po && <Badge tone="rose">over PO</Badge>}
              <div className="ml-auto flex gap-2">
                {i.status === 'submitted' && <><Button size="sm" onClick={() => act.mutate({ id: i.id, action: 'approve' })} data-testid={`inv-approve-${i.id}`}><Check className="mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => act.mutate({ id: i.id, action: 'reject' })}>Reject</Button></>}
                {i.status === 'approved' && <Button size="sm" onClick={() => act.mutate({ id: i.id, action: 'pay' })} data-testid={`inv-pay-${i.id}`}><CurrencyInr className="mr-1" /> Mark paid</Button>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default function Procurement() {
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="procurement-page">
      <div className="flex items-center gap-3 mb-1"><Briefcase className="text-blue-600" size={26} /><h1 className="font-display text-2xl font-extrabold">Vendors & Procurement</h1></div>
      <p className="text-sm text-slate-500 mb-4">Vendor identity, purchase orders and accounts-payable invoices with a self-service vendor invoice portal.</p>
      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="pos" data-testid="tab-pos">Purchase Orders</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="vendors"><VendorsTab /></TabsContent>
        <TabsContent value="pos"><POsTab /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
